// =============================================================================
// DZIEŃ KALENDARZOWY — arytmetyka dni okresu listy dokumentów (UD-12a)
//
// Moduł BEZ IMPORTÓW, celowo: bramka `dzienWInnejStrefie.test.ts` uruchamia go
// w osobnym procesie (`tsx`) pod obcą strefą czasową, bo w `Europe/Warsaw`
// (strefa testów) pomyłka „dzień przez `Date` w strefie przeglądarki” jest
// niewidzialna — w Warszawie północ UTC i północ lokalna leżą w tej samej
// dobie. Moduł z importami ciągnąłby do tamtego procesu pół powłoki.
//
// DWIE RZECZY, KTÓRE ŁATWO ZEPSUĆ:
//
//  * DZIEŃ TO NAPIS, NIE CHWILA. `data_operacji` jest kolumną `date`
//    („2026-10-15”). `new Date('2026-10-15')` to północ UTC, czyli w Los
//    Angeles 14 października — dzień przesunięty po cichu. Formatowanie
//    i arytmetyka idą więc na napisie i `Date.UTC`, bez godzin i bez strefy
//    przeglądarki.
//  * STREFA Z BAZY NIE JEST ZAUFANA. `locations.timezone` bywa NULL, a
//    `Intl.DateTimeFormat` z `timeZone: null` albo nieznaną strefą rzuca
//    `RangeError` (zmierzone na Node 20 i 22). `strefaBezpieczna` zamienia
//    każdą taką wartość na „strefa przeglądarki”, zamiast wywalić ekran.
// =============================================================================

/** Najdłuższy okres listy (dni, włącznie z obu stron) — dłuższy odrzuca walidacja. */
export const MAKS_DNI_OKRESU = 93;
/** Okres domyślny: ostatnie 7 dni, dziś włącznie. */
export const DNI_OKRESU_DOMYSLNEGO = 7;

const WZOR_DNIA = /^(\d{4})-(\d{2})-(\d{2})$/;
const DOBA_MS = 86_400_000;

/** „2026-10-15” → chwila północy UTC tego dnia albo `null` dla napisu, który dniem nie jest. */
function poludnikDnia(s: string): number | null {
  const m = WZOR_DNIA.exec(s);
  if (!m) return null;
  const [r, mies, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const chwila = Date.UTC(r, mies - 1, d);
  const z = new Date(chwila);
  // 2026-02-30 → 2 marca: poprawny napis, nieistniejący dzień.
  if (z.getUTCFullYear() !== r || z.getUTCMonth() !== mies - 1 || z.getUTCDate() !== d) return null;
  return chwila;
}

const dwie = (n: number) => String(n).padStart(2, '0');

function zPoludnika(chwila: number): string {
  const z = new Date(chwila);
  return `${z.getUTCFullYear()}-${dwie(z.getUTCMonth() + 1)}-${dwie(z.getUTCDate())}`;
}

/** Czy napis to istniejący dzień w postaci `RRRR-MM-DD`. */
export function czyDzien(s: string): boolean {
  return poludnikDnia(s) !== null;
}

/**
 * „2026-10-15” → „15.10.2026” — NAPISEM, bez `Date` w strefie przeglądarki.
 * Napis, który dniem nie jest, wraca bez zmian: to dosłowna wartość z bazy, a nie
 * wymyślona data.
 */
export function formatujDzien(s: string): string {
  const m = WZOR_DNIA.exec(s);
  return m && czyDzien(s) ? `${m[3]}.${m[2]}.${m[1]}` : s;
}

/** Dzień przesunięty o `n` dni (arytmetyka kalendarzowa, odporna na zmianę czasu). */
export function dodajDni(s: string, n: number): string {
  const chwila = poludnikDnia(s);
  if (chwila === null) throw new RangeError(`„${s}” nie jest dniem RRRR-MM-DD`);
  return zPoludnika(chwila + n * DOBA_MS);
}

/** Dzień następny — górna, WYŁĄCZNA granica okresu w zapytaniu (`lt`). */
export function dzienPo(s: string): string {
  return dodajDni(s, 1);
}

/** Liczba dni od `od` do `doDnia` włącznie z obu stron (`od` = `doDnia` → 1). */
export function dniOkresu(od: string, doDnia: string): number {
  const a = poludnikDnia(od);
  const b = poludnikDnia(doDnia);
  if (a === null || b === null) return Number.NaN;
  return Math.round((b - a) / DOBA_MS) + 1;
}

/**
 * Strefa nadająca się do `Intl` albo `undefined` (= strefa przeglądarki).
 * NULL, pusty napis i strefa, której silnik nie zna, dają `undefined` zamiast
 * `RangeError` w środku rysowania ekranu.
 */
export function strefaBezpieczna(strefa: string | null | undefined): string | undefined {
  if (typeof strefa !== 'string' || strefa.trim() === '') return undefined;
  try {
    new Intl.DateTimeFormat('pl-PL', { timeZone: strefa });
    return strefa;
  } catch {
    return undefined;
  }
}

/** Dzień kalendarzowy chwili w danej strefie (NULL / nieznana → strefa przeglądarki). */
export function dzienWStrefie(chwila: number | string, strefa: string | null | undefined): string {
  const czesci = new Intl.DateTimeFormat('en-US', {
    timeZone: strefaBezpieczna(strefa),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(chwila));
  const wez = (typ: string) => czesci.find((c) => c.type === typ)?.value ?? '';
  return `${wez('year')}-${wez('month')}-${wez('day')}`;
}

/** Ostatnie `dni` dni, dziś włącznie: `doDnia` = dziś w strefie, `od` = dziś − (dni − 1). */
export function okresOstatnichDni(
  teraz: number,
  strefa: string | null | undefined,
  dni: number
): { od: string; doDnia: string } {
  const dzis = dzienWStrefie(teraz, strefa);
  return { od: dodajDni(dzis, -(dni - 1)), doDnia: dzis };
}

/** Okres domyślny: `doDnia` = dziś w strefie, `od` = dziś − 6 dni. */
export function okresDomyslny(
  teraz: number,
  strefa: string | null | undefined
): { od: string; doDnia: string } {
  return okresOstatnichDni(teraz, strefa, DNI_OKRESU_DOMYSLNEGO);
}

export type WynikOkresu =
  | { ok: true; od: string; doDnia: string; doWylacznie: string; dni: number }
  | { ok: false; powod: string };

/**
 * Okres z pól dat: oba dni poprawne, `od` ≤ `doDnia`, najwyżej 93 dni.
 * Odmowa ma zdanie, które stoi przy polach — zapytanie listy wtedy nie wychodzi.
 */
export function walidujOkres(od: string, doDnia: string): WynikOkresu {
  if (!czyDzien(od)) return { ok: false, powod: 'Podaj poprawny pierwszy dzień okresu.' };
  if (!czyDzien(doDnia)) return { ok: false, powod: 'Podaj poprawny ostatni dzień okresu.' };
  const dni = dniOkresu(od, doDnia);
  if (dni < 1) {
    return { ok: false, powod: 'Pierwszy dzień okresu nie może być późniejszy niż ostatni.' };
  }
  if (dni > MAKS_DNI_OKRESU) {
    return {
      ok: false,
      powod: `Okres może mieć najwyżej ${MAKS_DNI_OKRESU} dni — skróć go.`,
    };
  }
  return { ok: true, od, doDnia, doWylacznie: dzienPo(doDnia), dni };
}
